import React, { useState, useRef, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { Building2, Calendar, ChevronRight, Sparkles } from 'lucide-react';
import { experiences } from '../data/mock';
import { SECTIONS, createSectionVariants, TIMING, EASING } from '../lib/motionDirector';

const ExperienceCard = ({ exp, index, isInView: sectionInView }) => {
  const cardRef = useRef(null);
  const isCardInView = useInView(cardRef, { 
    once: false, 
    margin: '-30% 0px -30% 0px',
    amount: 0.3
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Smooth auto-expand when card comes into view
  useEffect(() => {
    if (isCardInView && !isExpanded) {
      const timer = setTimeout(() => {
        setIsExpanded(true);
      }, 300 + index * 150); // Gentle staggered delay
      return () => clearTimeout(timer);
    }
  }, [isCardInView, isExpanded, index]);

  // Array of kanji for timeline markers (like hanko seals)
  const timelineKanji = ['戦', '功', '勲', '歴', '誉'];

  // Motion Director - Single card variant
  const cardVariants = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { 
        duration: SECTIONS.experience.duration,
        ease: EASING.cinematic
      }
    }
  };

  return (
    <motion.div
      ref={cardRef}
      variants={cardVariants}
      className={`relative mb-12 md:mb-16 ${index % 2 === 0 ? 'md:pr-1/2' : 'md:pl-1/2 md:ml-auto'}`}
    >
      <div className={`md:w-[calc(50%-2rem)] ${index % 2 === 0 ? '' : 'md:ml-auto'}`}>
        {/* Enhanced Kanji Timeline Marker (Hanko Seal Style) */}
        <motion.div 
          className="absolute left-4 md:left-1/2 -translate-x-1/2 z-10"
          initial={{ scale: 0, rotate: -90 }}
          animate={sectionInView ? { scale: 1, rotate: 0 } : {}}
          transition={{ 
            duration: 0.5, 
            delay: index * 0.15 + 0.1,
            ease: [0.34, 1.56, 0.64, 1] // Smooth bounce
          }}
        >
          <div className="relative w-12 h-12">
            {/* Hanko seal square border */}
            <div className="absolute inset-0 border-3 border-red-700 bg-black rotate-45" />
            <div className="absolute inset-1 border-2 border-amber-600/60 bg-gradient-to-br from-red-900/80 to-black rotate-45" />
            
            {/* Kanji character */}
            <div className="absolute inset-0 flex items-center justify-center">
              <motion.span 
                className="text-xl font-bold text-amber-100"
                animate={isCardInView ? {
                  textShadow: [
                    '0 0 0px rgba(251, 191, 36, 0)',
                    '0 0 8px rgba(251, 191, 36, 0.8)',
                    '0 0 0px rgba(251, 191, 36, 0)'
                  ]
                } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {timelineKanji[index % timelineKanji.length]}
              </motion.span>
            </div>
            
            {/* Outer pulse ring for active state */}
            <motion.div
              className="absolute -inset-2 border-2 border-red-700/40 rounded-sm rotate-45"
              animate={isCardInView ? {
                scale: [1, 1.3, 1],
                opacity: [0.6, 0, 0.6],
              } : {}}
              transition={{
                duration: 2,
                repeat: Infinity,
                delay: index * 0.5,
              }}
            />
          </div>
        </motion.div>

        {/* Connecting line animation */}
        <motion.div
          className={`hidden md:block absolute top-2 h-0.5 bg-gradient-to-r ${
            index % 2 === 0 
              ? 'right-0 w-8 from-transparent to-red-700/50' 
              : 'left-0 w-8 from-red-700/50 to-transparent'
          }`}
          style={{ 
            [index % 2 === 0 ? 'right' : 'left']: 'calc(50% + 0.5rem)',
          }}
          initial={{ scaleX: 0 }}
          animate={sectionInView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.5, delay: index * 0.3 + 0.4 }}
        />

        {/* Card */}
        <motion.div
          className={`ml-10 md:ml-0 p-6 bg-zinc-900/50 border rounded-lg transition-all duration-700 cursor-pointer ${
            isExpanded 
              ? 'border-amber-700/50 shadow-lg shadow-amber-900/20' 
              : 'border-amber-900/20 hover:border-amber-700/40'
          }`}
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ 
            scale: 1.01,
            boxShadow: '0 0 20px rgba(139, 0, 0, 0.15)',
            transition: { duration: 0.3 }
          }}
          whileTap={{ scale: 0.99 }}
        >
          {/* Animated border glow on active */}
          {isExpanded && (
            <motion.div
              className="absolute inset-0 rounded-lg pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: 'linear-gradient(90deg, transparent, rgba(139, 0, 0, 0.1), transparent)',
                backgroundSize: '200% 100%',
              }}
            />
          )}

          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-4 relative">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <motion.div
                  animate={exp.type === 'current' ? { rotate: [0, 10, -10, 0] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Building2 className="w-4 h-4 text-red-600" />
                </motion.div>
                <span className="text-sm tracking-wider text-amber-500">{exp.company}</span>
                {exp.type === 'current' && (
                  <motion.span 
                    className="px-2 py-0.5 text-[10px] tracking-wider bg-green-900/30 text-green-400 border border-green-700/40 rounded-full"
                    animate={{ 
                      boxShadow: ['0 0 0px rgba(34, 197, 94, 0)', '0 0 10px rgba(34, 197, 94, 0.5)', '0 0 0px rgba(34, 197, 94, 0)']
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    CURRENT
                  </motion.span>
                )}
              </div>
              <h3 className="font-bebas text-2xl tracking-wider text-amber-50">{exp.role}</h3>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 90 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <ChevronRight className="w-5 h-5 text-amber-500" />
            </motion.div>
          </div>

          {/* Period */}
          <div className="flex items-center gap-2 text-sm text-amber-100/50 mb-4">
            <Calendar className="w-4 h-4" />
            <span>{exp.period}</span>
          </div>

          {/* Description with smooth accordion animation */}
          <motion.div
            initial={false}
            animate={{ 
              height: isExpanded ? 'auto' : 0, 
              opacity: isExpanded ? 1 : 0 
            }}
            transition={{ 
              height: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
              opacity: { duration: 0.4, delay: isExpanded ? 0.1 : 0 }
            }}
            className="overflow-hidden"
          >
            <ul className="space-y-3 pt-2">
              {exp.description.map((item, i) => (
                <motion.li 
                  key={i} 
                  className="flex gap-3 text-sm text-amber-100/70"
                  initial={{ opacity: 0, y: 10 }}
                  animate={isExpanded ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
                  transition={{ 
                    duration: 0.4, 
                    delay: isExpanded ? i * 0.05 : 0,
                    ease: 'easeOut'
                  }}
                >
                  <motion.span 
                    className="text-red-600 mt-1.5"
                    animate={isExpanded ? { scale: [1, 1.1, 1] } : {}}
                    transition={{ duration: 0.6, delay: i * 0.05 }}
                  >
                    •
                  </motion.span>
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  // Motion Director orchestrates the section
  const variants = createSectionVariants(SECTIONS.experience);

  return (
    <section id="experience" className="relative py-24 md:py-32 bg-zinc-950 overflow-hidden" ref={ref}>
      {/* Warmer golden-orange gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-orange-950/15 via-amber-900/8 to-yellow-950/12" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,rgba(217,119,6,0.1),transparent_60%)]" />
      {/* Kanji Typography Layer - Large watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="text-[25rem] font-bold text-amber-100/[0.03] select-none tracking-wider drop-shadow-[0_0_100px_rgba(220,38,38,0.2)]">歴</div>
      </div>

      {/* Background decoration */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 right-10 text-[15rem] font-bold text-amber-100 select-none">戦</div>
        <div className="absolute bottom-20 left-10 text-[12rem] font-bold text-red-900 select-none">功</div>
      </div>

      <motion.div 
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8"
        variants={variants.container}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {/* Section Header */}
        <motion.div
          variants={variants.item}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1 text-xs tracking-[0.3em] text-amber-500/80 border border-amber-900/40 rounded-full mb-4">
            BATTLES FOUGHT
          </span>
          <h2 className="font-bebas text-4xl md:text-6xl tracking-wider text-amber-50">
            THE PATH I'VE WALKED
          </h2>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Central animated line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px overflow-hidden">
            {/* Base line */}
            <div className="absolute inset-0 bg-gradient-to-b from-red-700/50 via-amber-800/30 to-transparent" />
            
            {/* Animated glowing line */}
            <motion.div
              className="absolute left-0 w-full bg-gradient-to-b from-red-500 via-amber-500 to-red-500"
              style={{ height: '150px' }}
              initial={{ top: '-150px' }}
              animate={isInView ? { top: ['0%', '100%'] } : {}}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
                repeatDelay: 2,
              }}
            />
            
            {/* Spark particles along the line */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute left-1/2 -translate-x-1/2 w-2 h-2"
                initial={{ top: '0%', opacity: 0, scale: 0 }}
                animate={isInView ? {
                  top: ['0%', '100%'],
                  opacity: [0, 1, 1, 0],
                  scale: [0, 1, 1, 0],
                } : {}}
                transition={{
                  duration: 4,
                  delay: i * 1.5,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              >
                <Sparkles className="w-full h-full text-amber-400" />
              </motion.div>
            ))}
          </div>

          {experiences.map((exp, index) => (
            <ExperienceCard 
              key={exp.id} 
              exp={exp} 
              index={index} 
              isInView={isInView}
            />
          ))}
          
          {/* End of timeline marker */}
          <motion.div
            className="absolute left-4 md:left-1/2 -translate-x-1/2 bottom-0"
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: experiences.length * 0.3 + 0.5 }}
          >
            <motion.div
              className="w-3 h-3 bg-amber-700/50 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.5, 0.8, 0.5],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default Experience;
